import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Download, Check } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface PluginFreeDownloadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pluginName: string;
  downloadUrl: string;
}

export const PluginFreeDownloadDialog = ({ 
  open, 
  onOpenChange, 
  pluginName,
  downloadUrl 
}: PluginFreeDownloadDialogProps) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDownload = async () => {
    if (!email || !email.includes('@')) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    if (!name.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter your name.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Store the download info in Supabase
      const { error: dbError } = await supabase
        .from('wordpress_downloads')
        .insert({
          name: name.trim(),
          email: email.trim(),
          plugin_name: pluginName
        });

      if (dbError) {
        console.error('Error saving download:', dbError);
      }
      
      // Trigger the download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${pluginName.toLowerCase().replace(/\s+/g, '-')}-free.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Show toast with clickable link
      toast({
        title: "Download Started",
        description: "Your download should start automatically. If not, click here to download.",
        action: (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => {
              const retryLink = document.createElement('a');
              retryLink.href = downloadUrl;
              retryLink.download = `${pluginName.toLowerCase().replace(/\s+/g, '-')}-free.zip`;
              document.body.appendChild(retryLink);
              retryLink.click();
              document.body.removeChild(retryLink);
            }}
          >
            Download
          </Button>
        ),
      });

      // Close the dialog after a short delay
      setTimeout(() => {
        onOpenChange(false);
        setEmail('');
        setName('');
      }, 2000);
    } catch (error) {
      toast({
        title: "Download Failed",
        description: "Failed to start download. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const freeFeatures = [
    '30-day activity retention',
    'Basic activity logging',
    'Basic undo functionality',
    'Daily email digest',
    'CSV export',
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-2xl">Download Free Version</DialogTitle>
          <DialogDescription>
            Get started with {pluginName} for free
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Free Features */}
          <div className="bg-muted/30 rounded-lg p-4 space-y-2">
            <h4 className="font-semibold mb-3">Free version includes:</h4>
            <div className="space-y-2">
              {freeFeatures.map((feature, index) => (
                <div key={index} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <span className="text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Email Form */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <Button
            onClick={handleDownload}
            disabled={isSubmitting}
            className="w-full"
            size="lg"
          >
            <Download className="w-4 h-4 mr-2" />
            {isSubmitting ? 'Starting Download...' : 'Download Free Version'}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            Installation instructions will be sent to your email
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
